/**
 * The console's API client.
 *
 * Everything goes through the same-origin `/api` prefix, which the console Worker
 * proxies to the API Worker. That keeps the session cookie first-party and removes
 * the admin API's entire CORS surface — so `credentials` is `same-origin` and
 * there is no token to manage in the browser.
 *
 * Failures are mapped to a typed error rather than thrown as bare responses,
 * because the three cases need different UI: unauthenticated means show the
 * sign-in screen, forbidden means hide the action, and not-configured means the
 * deployment is incomplete and no amount of clicking will help.
 */

/**
 * Codes this client derives itself, for responses with no useful body.
 *
 * A server-supplied code is preferred and carried through verbatim, so this is a
 * subset rather than a closed set: the API owns that vocabulary and it will grow.
 */
export type ApiErrorCode =
	| 'unauthenticated'
	| 'forbidden'
	| 'not_configured'
	| 'not_found'
	| 'invalid_request'
	| 'network_error'
	| 'invalid_response'
	| 'unknown'

export class ApiError extends Error {
	readonly status: number
	/** The server's code when it sent one, otherwise one derived from the status. */
	readonly code: string
	/** Field-level issues from a schema rejection, when the server supplies them. */
	readonly issues: unknown

	constructor(status: number, code: string, message: string, issues?: unknown) {
		super(message)
		this.name = 'ApiError'
		this.status = status
		this.code = code
		this.issues = issues
	}

	/** The session is gone or never existed: show the sign-in screen. */
	get isUnauthenticated(): boolean {
		return this.status === 401
	}

	/** The deployment cannot serve this route at all. */
	get isNotConfigured(): boolean {
		return this.status === 503
	}

	get isForbidden(): boolean {
		return this.status === 403
	}
}

/** Maps an HTTP status onto a code, for responses with no useful body. */
export function codeForStatus(status: number): ApiErrorCode {
	if (status === 401) return 'unauthenticated'
	if (status === 403) return 'forbidden'
	if (status === 404) return 'not_found'
	if (status === 503) return 'not_configured'
	if (status === 400 || status === 422) return 'invalid_request'
	return 'unknown'
}

/** A human-readable message for a status, used when the server sends none. */
export function messageForStatus(status: number): string {
	if (status === 401) return 'Your session has expired. Sign in again to continue.'
	if (status === 403) return 'You do not have permission to do that.'
	if (status === 404) return 'That item no longer exists.'
	if (status === 503) return 'This deployment is not fully configured yet.'
	if (status >= 500) return 'Something went wrong on our side. Try again shortly.'
	return `Request failed with status ${status}`
}

interface ErrorBody {
	error?: unknown
	message?: unknown
	issues?: unknown
}

/**
 * Turns a failed response into the error the screens branch on.
 *
 * Everything here is defensive on purpose: a proxy or the platform can answer with
 * HTML the API never produced, and a UI that shows "Unexpected token <" is worse
 * than one that shows a status.
 */
export async function errorFromResponse(response: { status: number; text(): Promise<string> }): Promise<ApiError> {
	let parsed: unknown
	const text = await response.text()
	try {
		parsed = text === '' ? undefined : JSON.parse(text)
	} catch {
		parsed = undefined
	}

	const errorBody = (parsed ?? {}) as ErrorBody
	const serverCode = typeof errorBody.error === 'string' ? errorBody.error : undefined

	/* Trust the status over the body: a route that answers 401 without an `error`
	 * field still means "unauthenticated", and the UI must not fall back to a generic
	 * message for the one case it can actually act on. */
	const code = serverCode ?? codeForStatus(response.status)
	const message =
		typeof errorBody.message === 'string' && errorBody.message !== '' ? errorBody.message : messageForStatus(response.status)

	return new ApiError(response.status, code, message, errorBody.issues)
}

/** A network failure is not an HTTP status, and "status 0" explains nothing. */
export function networkError(cause: unknown): ApiError {
	return new ApiError(0, 'network_error', cause instanceof Error ? cause.message : 'Could not reach the server')
}
